import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";

export interface ProjectHours {
  budgeted: number;
  logged: number;
}

/**
 * Fetch aggregated budgeted and logged hours per project.
 * Returns a Map<projectId, { budgeted, logged }>.
 */
async function fetchProjectHours(): Promise<Map<string, ProjectHours>> {
  const [budgetData, timeData] = await Promise.all([
    fetchAllPaginated<{ project_id: string; quantity_hours: number }>(
      "budget_lines",
      "project_id, quantity_hours",
      (q) => q.eq("cost_type", "internal"),
    ),
    fetchAllPaginated<{ project_id: string; duration_seconds: number }>(
      "time_entries",
      "project_id, duration_seconds",
      (q) => q.is("deleted_at", null),
    ),
  ]);

  const map = new Map<string, ProjectHours>();

  for (const row of budgetData) {
    if (!row.project_id) continue;
    const entry = map.get(row.project_id) ?? { budgeted: 0, logged: 0 };
    entry.budgeted += row.quantity_hours ?? 0;
    map.set(row.project_id, entry);
  }

  for (const row of timeData) {
    if (!row.project_id) continue;
    const entry = map.get(row.project_id) ?? { budgeted: 0, logged: 0 };
    entry.logged += (row.duration_seconds ?? 0) / 3600;
    map.set(row.project_id, entry);
  }

  return map;
}

/**
 * Global aggregert oversikt over budsjetterte og loggede timer per prosjekt.
 * Brukes på ProjectsPage-listen. VIB-649: queryKey har ikke ID-suffiks fordi
 * dette er en list-key uten filter (alle prosjekter aggregert).
 */
export function useProjectHours() {
  return useQuery({
    queryKey: ["project-hours"],
    queryFn: fetchProjectHours,
    staleTime: 5 * 60 * 1000,
  });
}
